// built-from: /engagement-context-panel v1 impl:engagement-context-panel role:implementation
// The Context panel (feature 092-engagement-context-items): what the
// consultant has added for this engagement or this activity, and the small
// form that adds one more.
//
// 100 (owner decision 9): context has ONE home, the Context control in the
// chat dock. The panel reads as part of the conversation and carries its own
// controls in its own stylesheet, so it no longer borrows a working-area
// host's classes and no longer scrolls the working area behind it.
//
// Every API call goes through useEngagementContext; this file only renders.
// Files are checked against the shared attachment rules before anything is
// uploaded, so a Word document is refused here with the same words the chat
// uses and the server never sees it.
import { useId, useState, type ChangeEvent, type FormEvent } from 'react';
import { FileText, Paperclip } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  useEngagementContext,
  CONTEXT_LOAD_FAILED,
  type ContextItem,
} from '../hooks/useEngagementContext';
import {
  ACCEPTED_FILE_ACCEPT_ATTR,
  validateAttachmentBatch,
} from '../utils/attachmentValidation';
import '../styles/EngagementContextPanel.css';

export interface EngagementContextPanelProps {
  engagementId: string | null;
  activityId?: string;
}

function kindLabel(item: ContextItem): string {
  if (item.kind === 'text') return 'Text';
  const count = item.files.length;
  if (!count) return 'Files';
  return count === 1 ? '1 file' : `${count} files`;
}

function addedOn(iso: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

export function EngagementContextPanel({
  engagementId,
  activityId,
}: EngagementContextPanelProps): JSX.Element {
  const { items, status, error, refresh, addItem, removeItem } = useEngagementContext(
    engagementId,
    activityId,
  );
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [refusals, setRefusals] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [added, setAdded] = useState(false);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const instanceId = useId();
  const headingId = `ectx-heading-${activityId || 'engagement'}-${instanceId}`;

  const openForm = () => {
    setAdding(true);
    setAdded(false);
    setSaveError(null);
  };

  const closeForm = () => {
    setAdding(false);
    setTitle('');
    setText('');
    setFiles([]);
    setRefusals([]);
    setSaveError(null);
  };

  const onPickFiles = (event: ChangeEvent<HTMLInputElement>) => {
    const picked = event.target.files;
    if (!picked) return;
    const { accepted, errors } = validateAttachmentBatch(picked);
    if (accepted.length) setFiles((current) => [...current, ...accepted]);
    setRefusals(errors);
  };

  const onSave = async (event: FormEvent) => {
    event.preventDefault();
    if (saving || !title.trim()) return;
    setSaving(true);
    setSaveError(null);
    const result = await addItem({ title: title.trim(), text: text.trim(), files });
    setSaving(false);
    if (result.ok) {
      closeForm();
      setAdded(true);
      return;
    }
    setSaveError(result.message);
  };

  const onRemove = async (id: string) => {
    if (removing) return;
    setRemoving(true);
    setRemoveError(null);
    const result = await removeItem(id);
    setRemoving(false);
    setConfirmingId(null);
    if (!result.ok) setRemoveError(result.message);
  };

  return (
    <section className="ectx-panel" aria-labelledby={headingId}>
      <div className="ectx-heading">
        <h2 id={headingId}>
          {activityId ? 'Added for this activity' : 'Added for this engagement'}
        </h2>
        <span className="ectx-count">{items.length}</span>
      </div>

      {status === 'loading' ? (
        <p className="ectx-status" role="status">Loading context...</p>
      ) : null}

      {status === 'error' ? (
        <div className="ectx-alert" role="alert">
          <p>{error || CONTEXT_LOAD_FAILED}</p>
          <button
            type="button"
            className="ectx-button"
            onClick={() => { void refresh(); }}
          >
            Try again
          </button>
        </div>
      ) : null}

      {items.length ? (
        <ul className="ectx-list">
          {items.map((item) => (
            <li key={item.id} className="ectx-item">
              <div className="ectx-item-row">
                <span className="ectx-item-icon" aria-hidden="true">
                  {item.kind === 'text'
                    ? <FileText size={14} />
                    : <Paperclip size={14} />}
                </span>
                <div className="ectx-item-main">
                  <strong className="ectx-item-title">{item.title}</strong>
                  <span className="ectx-item-meta">
                    <span>{kindLabel(item)}</span>
                    {addedOn(item.addedAt) ? <span>{addedOn(item.addedAt)}</span> : null}
                    {item.addedBy ? <span>{`Added by ${item.addedBy}`}</span> : null}
                  </span>
                </div>
                <div className="ectx-item-actions">
                  <Link
                    className="ectx-item-link"
                    to={item.pagePath ? `/engagement/files/${item.pagePath}` : '/engagement/files'}
                  >
                    Open in Files
                  </Link>
                  {confirmingId === item.id ? null : (
                    <button
                      type="button"
                      className="ectx-button"
                      onClick={() => { setConfirmingId(item.id); setRemoveError(null); }}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
              {confirmingId === item.id ? (
                <div className="ectx-confirm" role="group" aria-label="Remove context">
                  <p>Remove this context? The files and its page are removed from the knowledge base.</p>
                  <div className="ectx-confirm-actions">
                    <button
                      type="button"
                      className="ectx-button ectx-button--primary"
                      disabled={removing}
                      onClick={() => { void onRemove(item.id); }}
                    >
                      {removing ? 'Removing...' : 'Remove'}
                    </button>
                    <button
                      type="button"
                      className="ectx-button"
                      onClick={() => setConfirmingId(null)}
                    >
                      Keep it
                    </button>
                  </div>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      {removeError ? <p className="ectx-alert ectx-alert--inline" role="alert">{removeError}</p> : null}
      {added && !adding ? <p className="ectx-added" role="status">Context added.</p> : null}

      {status === 'ready' && !items.length && !adding ? (
        <div className="ectx-empty">
          <p>No context added yet. Add notes, documents or anything the team should have to hand.</p>
          <button type="button" className="ectx-button ectx-button--primary" onClick={openForm}>
            Add context
          </button>
        </div>
      ) : null}

      {items.length > 0 && !adding ? (
        <button type="button" className="ectx-button" onClick={openForm}>
          Add context
        </button>
      ) : null}

      {adding ? (
        <form className="ectx-form" onSubmit={onSave} aria-label="Add context">
          <label className="ectx-field">
            <span>Title</span>
            <input
              type="text"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="What is this?"
            />
          </label>
          <label className="ectx-field">
            <span>Text</span>
            <textarea
              rows={4}
              value={text}
              onChange={(event) => setText(event.target.value)}
              placeholder="Paste notes, a decision, or anything worth keeping."
            />
          </label>
          <label className="ectx-field">
            <span>Files</span>
            <input
              type="file"
              multiple
              accept={ACCEPTED_FILE_ACCEPT_ATTR}
              onChange={onPickFiles}
            />
          </label>
          <p className="ectx-hint">
            PDF, images, Markdown, plain text, CSV or JSON. Save a Word document as PDF first.
          </p>

          {files.length ? (
            <ul className="ectx-chosen">
              {files.map((file, index) => (
                <li key={`${file.name}-${index}`}>
                  <span>{file.name}</span>
                  <button
                    type="button"
                    className="ectx-chip-remove"
                    aria-label={`Remove ${file.name}`}
                    onClick={() => setFiles((current) => current.filter((_, i) => i !== index))}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          ) : null}

          {refusals.map((refusal) => (
            <p className="ectx-alert ectx-alert--inline" role="alert" key={refusal}>{refusal}</p>
          ))}
          {saveError ? (
            <p className="ectx-alert ectx-alert--inline" role="alert">{saveError}</p>
          ) : null}

          <div className="ectx-form-actions">
            <button
              type="submit"
              className="ectx-button ectx-button--primary"
              disabled={saving || !title.trim()}
            >
              {saving ? 'Adding...' : 'Save'}
            </button>
            <button
              type="button"
              className="ectx-button"
              onClick={closeForm}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : null}

      {activityId ? (
        <p className="ectx-note">
          Engagement-wide context lives on the <Link to="/engagement/brief">Brief</Link>.
        </p>
      ) : null}
    </section>
  );
}

export default EngagementContextPanel;
